import React from 'react'
import toast from 'react-hot-toast'
import i18n from '../../../i18n'
import { io } from 'socket.io-client'
import apiService from '../../../services/api'
import useAppStore from '../../../store/useAppStore'
import { isDevelopmentMode } from '../../../utils/mockData'
import { CRASH_WS_URL, CRASH_SOCKET_PATH, CRASH_DEV_TOKEN, CRASH_DEV_CHAT_ID } from '../config/crashConfig'

/**
 * Общий игровой WebSocket Service (singleton)
 *
 * Подключение к игровому сокету на отдельном домене (§1 краш-документации).
 * Авторизация — тот же токен, что уходит в заголовке X-Telegram-Init-Data.
 *
 * Поведение:
 * - в dev-режиме (isDevelopmentMode) используем token: 'dev' — бэкенд тестового
 *   стенда подставит chat_id=1 (общий тестовый аккаунт);
 * - в проде/telegram-режиме берём токен из apiService.getAuthContext().token;
 * - на connect повторяем subscribe для активных игр — сервер пришлёт снапшот состояния
 *   (§8 краш-документации).
 */
class CrashSocketService {
  constructor() {
    this.socket = null
    this.authToken = null
    this.hasAuthenticatedSession = false
    // Список обработчиков событий для снятия подписок при уходе с экрана
    this.listeners = new Map()
    this.subscriptions = new Map()
    this.subscribedGames = new Set()
    this.clockOffset = 0
    this.onConnectCallbacks = []
    this.onDisconnectCallbacks = []
  }

  /**
   * Получить токен для авторизации сокета.
   * В dev — 'dev', в проде — токен из auth context.
   */
  resolveAuthToken() {
    try {
      const authContext = apiService.getAuthContext()
      if (authContext?.mode === 'development' && isDevelopmentMode()) return CRASH_DEV_TOKEN
      return authContext?.isAuthorized ? authContext.token : null
    } catch (error) {
      console.error('❌ Crash socket: не удалось получить токен авторизации', error)
      return null
    }
  }

  /**
   * chatId текущего игрока — чтобы найти свою ставку в crash:players до первого crash:bet.
   * В dev-режиме тестовый стенд подставляет chat_id=1 (§1a), в остальных — id из auth context.
   */
  resolveChatId() {
    try {
      const authContext = apiService.getAuthContext()
      if (authContext?.mode === 'development' && isDevelopmentMode()) return CRASH_DEV_CHAT_ID
      return authContext?.userId ?? null
    } catch (error) {
      console.error('❌ Crash socket: не удалось получить chatId игрока', error)
      return null
    }
  }

  /**
   * Создать socket.io-инстанс с актуальным токеном.
   * Не подключает автоматически — для этого вызвать .connect().
   */
  createSocket() {
    const token = this.resolveAuthToken()
    this.authToken = token
    this.hasAuthenticatedSession = false

    if (!token) {
      console.warn('⚠️ Crash socket: нет токена — подключаюсь в режиме наблюдателя (ставки будут игнорироваться)')
    }

    this.socket = io(CRASH_WS_URL, {
      path: CRASH_SOCKET_PATH,
      transports: ['websocket'],     // polling как фолбэк допустим, но websocket первым
      auth: { token },               // не светится в логах, в отличие от ?token=
      reconnection: true,            // не выключать — socket.io сам переподключается
      autoConnect: false,
      // Настройки heartbeat из спеки (§8.1): быстрее замечает обрыв
      pingInterval: 10000,
      pingTimeout: 8000
    })

    // Проброс событий подключения наружу
    this.socket.on('connect', () => {
      this.subscribedGames.clear()
      this.subscriptions.forEach((count, game) => {
        if (count > 0) {
          this.socket.emit('subscribe', { game })
          this.subscribedGames.add(game)
        }
      })
      this.onConnectCallbacks.forEach((cb) => {
        try { cb() } catch (e) { console.error('Crash socket connect callback error:', e) }
      })
    })

    this.socket.on('disconnect', (reason) => {
      console.warn('❌ Crash socket disconnected:', reason)
      this.onDisconnectCallbacks.forEach((cb) => {
        try { cb(reason) } catch (e) { console.error('Crash socket disconnect callback error:', e) }
      })
    })

    this.socket.on('connect_error', (error) => {
      console.error('Game socket connection error:', error.message || error)
    })
    this.socket.on('user:alert', ({ code }) => {
      if (code !== 'rate_limited') return
      toast.error(React.createElement('div', { className: 'flex flex-col gap-2' },
        React.createElement('span', null, i18n.t('guest.socketLimit')),
        React.createElement('button', { type: 'button', onClick: () => window.location.reload(), className: 'rounded-lg border border-dark-primary px-3 py-2 font-bold' }, i18n.t('common.retry'))
      ), { id: 'guest-socket-limit', duration: Infinity })
    })
    this.socket.on('server:time', ({ serverTime }) => {
      if (Number.isFinite(Number(serverTime))) this.clockOffset = Number(serverTime) - Date.now()
    })
    this.socket.on('user:balance', ({ balance }) => {
      if (this.authToken && this.resolveAuthToken() === this.authToken && balance != null && Number.isFinite(Number(balance))) {
        this.hasAuthenticatedSession = true
        useAppStore.getState().setBalance(balance)
      }
    })
    this.socket.on('crash:balance', ({ balance }) => {
      if (this.authToken && balance != null && Number.isFinite(Number(balance))) this.hasAuthenticatedSession = true
    })

    return this.socket
  }

  /**
   * Подключиться. Если уже подключён — ничего не делает.
   */
  connect() {
    if (!this.socket) this.createSocket()
    else if (this.resolveAuthToken() !== this.authToken) {
      this.refreshAuthToken()
      return this.socket
    }
    if (!this.socket.connected) this.socket.connect()
    return this.socket
  }

  /**
   * Отключиться и очистить подписки.
   */
  disconnect() {
    if (this.subscriptions.size > 0) return
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
    this.hasAuthenticatedSession = false
    this.listeners.clear()
    this.subscribedGames.clear()
  }

  subscribe(game) {
    const count = this.subscriptions.get(game) || 0
    this.subscriptions.set(game, count + 1)
    this.connect()
    if (count !== 0 || !this.socket.connected) return
    this.socket.emit('subscribe', { game })
    this.subscribedGames.add(game)
  }

  unsubscribe(game) {
    const count = this.subscriptions.get(game) || 0
    if (count > 1) this.subscriptions.set(game, count - 1)
    else if (count === 1) {
      this.subscriptions.delete(game)
      if (this.socket?.connected) this.socket.emit('unsubscribe', { game })
      this.subscribedGames.delete(game)
      if (this.subscriptions.size === 0) this.disconnect()
    }
  }

  requestState(game) {
    if (!this.subscriptions.has(game)) return
    if (this.socket?.connected) this.socket.emit('subscribe', { game })
    else this.connect()
  }

  /**
   * Обновить токен и переподключиться.
   * Используется, когда initData протух (§1: «кнопка нажимается, ничего не происходит»).
   */
  refreshAuthToken() {
    const newToken = this.resolveAuthToken()
    if (!this.socket) {
      this.authToken = newToken
      return
    }
    this.authToken = newToken
    this.hasAuthenticatedSession = false
    this.socket.auth = { token: newToken }
    this.socket.disconnect().connect()
  }

  /**
   * Получить сырой socket.io-инстанс (для хука useCrashSocket).
   */
  getSocket() {
    return this.socket
  }

  /**
   * Подписка на событие сервера.
   * Сохраняет callback для последующей отписки.
   */
  on(event, callback) {
    if (!this.socket) {
      this.createSocket()
    }
    this.socket.on(event, callback)
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
  }

  /**
   * Отписка от события.
   */
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback)
    }
    const callbacks = this.listeners.get(event)
    if (callbacks) {
      const index = callbacks.indexOf(callback)
      if (index > -1) callbacks.splice(index, 1)
      if (callbacks.length === 0) this.listeners.delete(event)
    }
  }

  /**
   * Подписаться на событие connect (для сброса UI / ожидания снапшота).
   */
  onConnect(callback) {
    this.onConnectCallbacks.push(callback)
  }

  /**
   * Отписаться от connect.
   */
  offConnect(callback) {
    const i = this.onConnectCallbacks.indexOf(callback)
    if (i > -1) this.onConnectCallbacks.splice(i, 1)
  }

  /**
   * Подписаться на событие disconnect.
   */
  onDisconnect(callback) {
    this.onDisconnectCallbacks.push(callback)
  }

  /**
   * Отписаться от disconnect.
   */
  offDisconnect(callback) {
    const i = this.onDisconnectCallbacks.indexOf(callback)
    if (i > -1) this.onDisconnectCallbacks.splice(i, 1)
  }

  /**
   * Отправить событие на сервер.
   */
  emit(event, data) {
    if (!this.socket?.connected) return false
    this.socket.emit(event, data)
    return true
  }

  /**
   * Текущий статус подключения.
   */
  isConnected() {
    return Boolean(this.socket?.connected)
  }

  isAuthenticated() {
    return Boolean(this.socket?.connected && this.hasAuthenticatedSession && this.authToken && this.resolveAuthToken() === this.authToken)
  }
}

// Singleton — один игровой сокет на всё приложение
const crashSocket = new CrashSocketService()

export default crashSocket
